import React, { useEffect, useState, memo } from 'react'
import { useSelector } from 'react-redux'
import ShipmentTable from './ShipmentTable'
import UpcomingShipment from './UpcomingShipment'
import TotalShipment from './TotalShipment'
import CompletedShipment from './CompletedShipment'
import BilledShipments from './BilledShipments'
import ProcessStepper from './ProcessStepper'
import dayjs from 'dayjs'

const SelectedCard = memo(({ selectedCard, selectedYear = "all", selectedMonth = "all", selectedShipmentType = "all" }) => {
    const user = useSelector((state) => state.auth.user);
    const [searchTerm] = useState("");
    const [selectedShipment, setSelectedShipment] = useState(null);

    useEffect(() => {
        setSelectedShipment(null);
    }, [selectedCard, selectedYear, selectedMonth, selectedShipmentType]);

    // Get KPI status for waybill background color
    const getKPIStatus = (shipment) => {
        const shipmentType = shipment.shipment_type;

        // Only calculate KPI for AIR, SEA, and BROKERAGE shipments
        if (shipmentType !== "AIR" && shipmentType !== "SEA" && shipmentType !== "BROKERAGE") {
            return null;
        }

        // Find the "Arrival at Destination" remark to get the aad (Actual Arrival Date)
        const arrivalRemark = shipment.remarks?.find(
            (r) => r.status === "Arrival at Destination"
        );

        if (!arrivalRemark) {
            return null; // No arrival remark found
        }

        // Only use aad field - if missing, it's "No Arrival Data"
        if (!arrivalRemark.aad || arrivalRemark.aad === "" || arrivalRemark.aad === null) {
            return null; // No arrival date recorded yet
        }

        // Parse arrival date - handle various datetime formats
        let arrivalDate = dayjs(arrivalRemark.aad);

        // If standard parsing fails, try custom formats
        if (!arrivalDate.isValid()) {
            arrivalDate = dayjs(arrivalRemark.aad, [
                "YYYY-MM-DDTHH:mm:ss",
                "YYYY-MM-DDTHH:mm",
                "YYYY-MM-DDTHH:mm:ss.SSSZ",
            ]);
        }

        if (!arrivalDate.isValid()) {
            return null; // Invalid arrival date
        }

        // Find delivery/turnover date
        let completionDate;
        if (shipment.status === "Delivered" || shipment.status === "Turnover") {
            // Find the remark with Delivered or Turnover status
            const completionRemark = shipment.remarks?.find(
                (r) => r.status === "Delivered" || r.status === "Turnover"
            );

            if (completionRemark && completionRemark.date) {
                completionDate = dayjs(completionRemark.date);
            } else {
                // Fallback to current date if shipment is completed but no completion date
                completionDate = dayjs();
            }
        } else {
            // If not yet completed, use current date
            completionDate = dayjs();
        }

        // Calculate days between arrival and completion/current
        const daysDiff = completionDate.diff(arrivalDate, "day");

        // If arrival date is in the future, treat as "On Time" with 0 days
        if (daysDiff < 0) {
            return {
                status: "On Time",
                bgColor: "bg-green-100",
            };
        }

        // AIR shipment KPI (1-2 days is On Time, >2 days is Delayed)
        if (shipmentType === "AIR") {
            if (daysDiff >= 0 && daysDiff <= 2) {
                return {
                    status: "On Time",
                    bgColor: "bg-green-100",
                };
            } else if (daysDiff > 2) {
                return {
                    status: "Delayed",
                    bgColor: "bg-red-100",
                };
            }
        }

        // SEA shipment KPI (1-5 days On Time, 6-10 Slightly Delayed, >10 Delayed)
        if (shipmentType === "SEA") {
            if (daysDiff >= 0 && daysDiff <= 5) {
                return {
                    status: "On Time",
                    bgColor: "bg-green-100",
                };
            } else if (daysDiff >= 6 && daysDiff <= 10) {
                return {
                    status: "Slightly Delayed",
                    bgColor: "bg-orange-200",
                };
            } else if (daysDiff > 10) {
                return {
                    status: "Delayed",
                    bgColor: "bg-red-100",
                };
            }
        }

        // BROKERAGE KPI (same as SEA: 1-5 days On Time, 6-10 Slightly Delayed, >10 Delayed)
        if (shipmentType === "BROKERAGE") {
            if (daysDiff >= 0 && daysDiff <= 5) {
                return {
                    status: "On Time",
                    bgColor: "bg-green-100",
                };
            } else if (daysDiff >= 6 && daysDiff <= 10) {
                return {
                    status: "Slightly Delayed",
                    bgColor: "bg-orange-200",
                };
            } else if (daysDiff > 10) {
                return {
                    status: "Delayed",
                    bgColor: "bg-red-100",
                };
            }
        }

        return null;
    };

    // Enhanced placeholder component with modern design
    const ProcessStepperPlaceholder = () => (
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 w-full shadow-lg border border-gray-100 h-full flex flex-col items-center justify-center">
            {/* Animated Background Elements */}
            <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className="absolute top-4 right-4 w-16 h-16 bg-blue/5 rounded-full blur-xl opacity-60 animate-pulse"></div>
                <div className="absolute bottom-6 left-6 w-12 h-12 bg-yellow/5 rounded-full blur-lg opacity-40 animate-pulse delay-500"></div>
            </div>

            <div className="relative z-10 text-center space-y-6 max-w-sm">
                {/* Main Icon with Animated Ring */}
                <div className="relative mx-auto w-20 h-20">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue/10 to-blue/20 rounded-full animate-pulse"></div>
                    <div className="absolute inset-2 bg-gradient-to-br from-blue to-blue/90 rounded-full flex items-center justify-center shadow-lg">
                        <i className="fas fa-route text-white text-2xl"></i>
                    </div>
                    {/* Animated outer ring */}
                    <div className="absolute -inset-2 border-2 border-blue/20 rounded-full animate-ping opacity-30"></div>
                </div>

                {/* Title */}
                <div className="space-y-2">
                    <h3 className="text-xl font-bold text-blue">
                        Process Tracker
                    </h3>
                    <p className="text-grey text-sm font-medium">
                        Select a shipment to view the detailed process timeline
                    </p>
                </div>

                

                {/* Call to Action */}
                <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-center justify-center gap-2 text-blue text-sm font-medium">
                        <i className="fas fa-mouse-pointer text-xs animate-bounce"></i>
                        <span>Click any shipment from the table</span>
                    </div>
                </div>
            </div>

            {/* Subtle bottom decoration */}
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue/10 to-transparent rounded-b-2xl"></div>
        </div>
    );

    return (
        <div className="active-shipment-container grid grid-cols-1 lg:grid-cols-5 grid-rows-1 gap-4">
            <div className="active-table col-span-1 lg:col-span-3 row-span-1 p-3 bg-white shadow-md rounded-lg">
                <div className="active-table-container">
                    {selectedCard === "active" && (
                    <ShipmentTable
                        searchTerm={searchTerm}
                        onRowClick={(shipment) => setSelectedShipment(shipment)}
                        role={"superadmin"}
                        filterValue={"Everyone"}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        selectedShipmentType={selectedShipmentType}
                    />
                    )}
                    {selectedCard === "upcoming" && (
                    <UpcomingShipment
                        searchTerm={searchTerm}
                        onRowClick={(shipment) => setSelectedShipment(shipment)}
                        role={"superadmin"}
                        filterValue={"Everyone"}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        selectedShipmentType={selectedShipmentType}
                    />
                    )}
                    {selectedCard === "total" && (
                    <TotalShipment
                        searchTerm={searchTerm}
                        onRowClick={(shipment) => setSelectedShipment(shipment)}
                        role={"superadmin"}
                        filterValue={"Everyone"}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        selectedShipmentType={selectedShipmentType}
                    />
                    )}
                    {selectedCard === "cancelled" && (
                    <TotalShipment
                        searchTerm={searchTerm}
                        onRowClick={(shipment) => setSelectedShipment(shipment)}
                        role={"superadmin"}
                        filterValue={"Everyone"}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        selectedShipmentType={selectedShipmentType}
                        statusFilter={"cancelled"}
                        title={"Cancelled Shipments"}
                    />
                    )}
                    {selectedCard === "completed" && (
                    <CompletedShipment
                        searchTerm={searchTerm}
                        onRowClick={(shipment) => setSelectedShipment(shipment)}
                        role={"superadmin"}
                        filterValue={"Everyone"}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                        selectedShipmentType={selectedShipmentType}
                    />
                    )}
                    {selectedCard === "billed" && (
                    <BilledShipments
                        onRowClick={(shipment) => setSelectedShipment(shipment)}
                        role={"superadmin"}
                        filterValue={"Everyone"}
                        selectedYear={selectedYear}
                        selectedMonth={selectedMonth}
                    />
                    )}
                </div>
            </div>
           
            {/* Process Stepper */}
            <div className="stepper-container col-span-1 lg:col-start-4 lg:col-end-6 row-span-1">
                {selectedShipment ? (
                    <ProcessStepper
                        shipment={selectedShipment}
                        kpiColor={user?.role === "Corporate" ? null : getKPIStatus(selectedShipment)?.bgColor}
                    />
                ) : (
                    <ProcessStepperPlaceholder />
                )}
            </div>
        </div>
    )
})

SelectedCard.displayName = 'SelectedCard';

export default SelectedCard
